import { faArrowLeft, faCheck } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Typography } from 'src/components/Typography';
import { useTheme } from 'src/components/muiTheme/utils';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { Collapse } from 'src/components/transitions/Collapse';
import { CartItem as CartItemType } from 'src/models/cart/_types';
import {
  ItemBrowser,
  SectionedItemSelector,
  SidebarDialog,
  SidebarDialogContent,
  SidebarDialogFab,
  SidebarDialogSidebar,
} from 'src/shared/SidebarDialog';

import { PosConfigItemBrowser } from './PosConfigItemBrowser';
import {
  PosSelectedItem,
  usePosConfigItemSelector,
} from './usePosConfigItemSelector';

// Export for external use
export type { PosSelectedItem } from './usePosConfigItemSelector';

interface PosConfigItemSelectorDialogProps {
  open: boolean;
  type: 'pos' | 'kiosk';
  onClose: () => void;
  onSave: (items: PosSelectedItem[]) => void;
}

export const PosConfigItemSelectorDialog: React.FC<
  PosConfigItemSelectorDialogProps
> = ({ open, onClose, onSave, type }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'posConfig' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    selectedItemType,
    selectedSection,
    posItems,
    itemSearchValue,
    debouncedItemSearchValue,
    selectItemTypeHeader,
    sections,
    handleSelectItem,
    handleRemoveItem,
    handleSaveItems,
    handleBackClick,
    setItemSearchValue,
    checkIfItemAdded,
  } = usePosConfigItemSelector({
    type,
    onSave,
    onClose,
    isMobile,
  });

  const sidebarContent = (
    <SectionedItemSelector
      searchValue={itemSearchValue}
      onSearchChange={selectedItemType ? setItemSearchValue : undefined}
      searchPlaceholder={t('searchPlaceholder')}
      sections={sections}
      sx={{ mt: { sx: 1, md: 0 } }}
    />
  );

  const detailContent = selectedItemType ? (
    selectedSection === 'Library' ? (
      <ItemBrowser
        fromPosConfig={true}
        user={undefined}
        selectedItemType={selectedItemType}
        searchValue={itemSearchValue}
        onSearchChange={setItemSearchValue}
        debouncedSearchValue={debouncedItemSearchValue}
        onSelect={handleSelectItem}
        onRemove={handleRemoveItem}
        selectedItems={posItems as CartItemType[]}
        cart={null}
        includeOnlineTraining={false}
        checkIfItemAdded={checkIfItemAdded}
      />
    ) : selectedSection === 'Finders' ? (
      <PosConfigItemBrowser
        selectedItemType={selectedItemType}
        searchValue={itemSearchValue}
        onSearchChange={setItemSearchValue}
        debouncedSearchValue={debouncedItemSearchValue}
        onSelect={handleSelectItem}
        onRemove={handleRemoveItem}
        selectedItems={posItems as CartItemType[]}
      />
    ) : null
  ) : (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedSection === 'Finders'
          ? t('findItems', {
              itemType: selectedItemType || tCommon('labels.items'),
            })
          : t('selectFromSidebar')}
      </Typography>
      <Typography variant="body2">
        {selectedSection === 'Library'
          ? t('chooseFromLibrary')
          : selectedSection === 'Finders'
          ? t('selectFromFinders')
          : t('selectFromSidebarDetails')}
      </Typography>
    </Box>
  );

  const backButton = selectedItemType ? (
    <IconButton onClick={handleBackClick} color="secondary">
      <Icon icon={faArrowLeft} />
    </IconButton>
  ) : undefined;

  // Mobile content switching: on mobile, show sidebar content if no item type selected, otherwise show detail content
  const mainContent =
    isMobile && !selectedItemType ? sidebarContent : detailContent;

  return (
    <SidebarDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={selectItemTypeHeader}
      leftAdornment={backButton}
    >
      <SidebarDialogSidebar>{sidebarContent}</SidebarDialogSidebar>
      <SidebarDialogContent>{mainContent}</SidebarDialogContent>
      <SidebarDialogFab
        text={`${posItems.length} item${
          posItems.length === 1 ? '' : 's'
        } selected`}
        startAdornment={
          <Collapse orientation="horizontal" in={posItems.length > 0}>
            <Icon icon={faCheck} />
          </Collapse>
        }
        active={posItems.length > 0}
        onClick={handleSaveItems}
      />
    </SidebarDialog>
  );
};
